/* eslint-disable @typescript-eslint/no-use-before-define */
import { useMemo } from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { type CustomerItem } from '../../pages/customers';
import { Button } from '../design-system';
import { CustomerQuote } from '../global/CustomerQuote';

const Wrapper = styled.div<{ $isStory: boolean }>`
  display: flex;
  position: relative;
  flex: 0 1 calc(100% / 4);
  align-items: flex-start;
  flex-direction: column;
  margin: 0;
  width: calc(100% / 4);
  height: 200px;
  padding: 10px;
  background-color: ${({ theme }) => rgba(theme.backgroundColor, 0.35)};

  > a {
    display: flex;

    > div {
      width: 100%;
      min-width: 0;

      > div {
        width: 100%;
        min-width: 0;
        height: 100%;
      }
    }
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    flex: 0 1 calc(100% / 3);
    width: calc(100% / 3);
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    flex: 0 1 calc(100% / 2);
    width: calc(100% / 2);
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    flex: 0 1 100%;
    width: 100%;
  }

  ${({ $isStory }) =>
    $isStory &&
    css`
      flex: 1 1 0;
      min-height: 0;
      height: auto;
      flex: 0 1 calc(100% / 3);
      width: calc(100% / 3);

      > div {
        height: auto;
      }

      .customerInner {
        justify-content: flex-start;
      }

      @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
        flex: 0 1 calc(100% / 2);
        width: calc(100% / 2);
      }

      @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
        flex: 0 1 100%;
        width: 100%;
      }
    `}
`;

const StyledButton = styled(Button)`
  &&& {
    width: 100%;
    height: 100%;
    border: 1px solid ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.3)};
    padding: 0;
  }
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 30px;
  justify-content: center;

  p {
    font-family: ${({ theme }) => theme.fontFamily.SANS};
    white-space: normal;
    font-size: ${({ theme }) => theme.fontSize.MEDIUM};
    line-height: ${({ theme }) => theme.lineHeight.MEDIUM};
    margin: 0;
    text-transform: none;
    margin-bottom: 0;
  }
`;

interface CustomerGridItemProps {
  isManyMore?: boolean;
  isStory?: boolean;
  item: CustomerItem;
  loudStyle?: boolean;
}

export const CustomerGridItem: React.ComponentType<CustomerGridItemProps> =
  React.memo(
    ({ item, isStory = false, isManyMore = false, loudStyle = false }) => {
      const content =
        isStory && item.customer_story ? (
          <Inner>
            <CustomerQuote
              customerName={item.name}
              logoPath={item.logo.url}
              quote={item.customer_story[0].synopsis.synopsis}
            />
          </Inner>
        ) : (
          <Inner>
            {!isManyMore ? (
              <img alt={item.name} src={item.logo.url} />
            ) : (
              <div>... and many more!</div>
            )}
          </Inner>
        );

      const customerLink = useMemo(() => {
        if (isManyMore) {
          return undefined;
        }
        if (item.customer_story) {
          return `/customers/${item.slug}/`;
        }
        return item.website;
      }, [item, isManyMore]);

      return (
        <Wrapper $isStory={isStory}>
          <StyledButton
            disabled={isManyMore}
            link={customerLink}
            stylez={!loudStyle || !isStory ? 'minimal' : 'bold'}
            text={content}
          />
        </Wrapper>
      );
    }
  );
